import { useEffect, useMemo, useRef } from 'react';

import { c } from 'ttag';

import { useAddresses } from '@proton/account/addresses/hooks';
import { useUserKeys } from '@proton/account/userKeys/hooks';
import { useConfig } from '@proton/app-context/useConfig';
import { useNotifications } from '@proton/app-context/useNotifications';
import { Button } from '@proton/atoms/Button/Button';
import { Tooltip } from '@proton/atoms/Tooltip/Tooltip';
import { IcArrowUpFromSquare } from '@proton/icons/icons/IcArrowUpFromSquare';
import { IcPen } from '@proton/icons/icons/IcPen';
import { IcTrash } from '@proton/icons/icons/IcTrash';
import { useContact } from '@proton/mail/store/contacts/contactHooks';
import { useContactGroups } from '@proton/mail/store/labels/hooks';
import { useMailSettings } from '@proton/mail/store/mailSettings/hooks';
import { APPS } from '@proton/shared/lib/constants';
import { CRYPTO_PROCESSING_TYPES } from '@proton/shared/lib/contacts/constants';
import { singleExport } from '@proton/shared/lib/contacts/helpers/export';
import { textToClipboard } from '@proton/shared/lib/helpers/browser';
import { toMap } from '@proton/shared/lib/helpers/object';
import type { ContactEmail } from '@proton/shared/lib/interfaces/contacts/Contact';
import type { VCardContact } from '@proton/shared/lib/interfaces/contacts/VCard';

import Loader from '../../../components/loader/Loader';
import type { ModalProps } from '../../../components/modalTwo/Modal';
import ModalTwo from '../../../components/modalTwo/Modal';
import ModalTwoContent from '../../../components/modalTwo/ModalContent';
import ModalTwoFooter from '../../../components/modalTwo/ModalFooter';
import ModalTwoHeader from '../../../components/modalTwo/ModalHeader';
import { useLinkHandler } from '../../../hooks/useLinkHandler';
import ErrorBoundary from '../../app/ErrorBoundary';
import GenericError from '../../error/GenericError';
import type { ContactEditProps } from '../edit/ContactEditModal';
import type { ContactEmailSettingsProps } from '../email/ContactEmailSettingsModal';
import type { ContactGroupEditProps } from '../group/ContactGroupEditModal';
import useContactList from '../hooks/useContactList';
import useVCardContact from '../hooks/useVCardContact';
import type { ContactDeleteProps } from '../modals/ContactDeleteModal';
import ContactView from './ContactView';

export interface ContactDetailsProps {
    contactID: string;
    onMailTo?: (src: string) => void;
    onEdit: (props: ContactEditProps) => void;
    onDelete: (props: ContactDeleteProps) => void;
    onEmailSettings: (props: ContactEmailSettingsProps) => void;
    onGroupDetails: (contactGroupID: string) => void;
    onGroupEdit: (props: ContactGroupEditProps) => void;
    onUpgrade: () => void;
    onSignatureError: (contactID: string) => void;
    onDecryptionError: (contactID: string) => void;
}

type Props = ContactDetailsProps & ModalProps;

const ContactDetailsModal = ({
    contactID,
    onMailTo,
    onEdit,
    onDelete,
    onEmailSettings,
    onGroupDetails,
    onGroupEdit,
    onUpgrade,
    onSignatureError,
    onDecryptionError,
    ...rest
}: Props) => {
    const { onClose } = rest;

    const [mailSettings] = useMailSettings();
    const [contactGroups = [], loadingContactGroups] = useContactGroups();
    const [userKeysList, loadingUserKeys] = useUserKeys();
    const [addresses = [], loadingAddresses] = useAddresses();
    const { loading: loadingContacts, contactEmailsMap } = useContactList({});
    const [contact, loadingContact] = useContact(contactID);
    const modalRef = useRef<HTMLDivElement>(null);
    const { createNotification } = useNotifications();

    const { APP_NAME } = useConfig();

    const { modal: linkModal } = useLinkHandler(modalRef, mailSettings, { onMailTo });

    const {
        vCardContact,
        isLoading: loadingVCard,
        errors,
        isVerified,
        onReload,
    } = useVCardContact({ contact, userKeysList });

    // Close the modal on a click on a mailto, useLinkHandler will open the composer
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const link = (event.target as Element).closest('a');
            const src = link?.getAttribute('href');
            if (src?.startsWith('mailto:')) {
                if (APP_NAME === APPS.PROTONMAIL) {
                    onClose?.();
                } else {
                    textToClipboard(src?.replace('mailto:', ''));
                    createNotification({
                        text: c('Success').t`Email address copied to clipboard`,
                    });
                }
            }
        };

        modalRef.current?.addEventListener('click', handleClick);
        return () => modalRef.current?.removeEventListener('click', handleClick);
    }, []);

    const handleEdit = (newField?: string) => {
        onEdit({ contactID, vCardContact, newField });
    };

    const handleDelete = () => {
        onDelete({ contactIDs: [contactID] });
        onClose?.();
    };

    const handleExport = () => {
        const hasError = errors?.some(
            (error) => error instanceof Error || error.type !== CRYPTO_PROCESSING_TYPES.SIGNATURE_NOT_VERIFIED
        );

        if (hasError) {
            createNotification({ text: c('Error').t`Cannot export this contact`, type: 'error' });
            return;
        }

        return singleExport(vCardContact as VCardContact);
    };

    const ownAddresses = useMemo(() => addresses.map(({ Email }) => Email), [addresses]);
    const contactGroupsMap = useMemo(() => toMap(contactGroups), [contactGroups]);

    const isLoading =
        loadingContacts ||
        loadingContactGroups ||
        loadingUserKeys ||
        loadingAddresses ||
        loadingContact ||
        loadingVCard;

    return (
        // NB: actual modal size defined by custom CSS targeting `.contacts-modal`
        <ModalTwo size="large" className="contacts-modal" data-testid="contact-details-modal" {...rest}>
            <ModalTwoHeader
                actions={[
                    <Tooltip title={c('Action').t`Edit`}>
                        <Button
                            color="weak"
                            shape="ghost"
                            icon
                            onClick={() => handleEdit()}
                            className="inline-flex ml-2"
                            data-testid="contact-details:edit"
                        >
                            <IcPen alt={c('Action').t`Edit`} />
                        </Button>
                    </Tooltip>,
                    <Tooltip title={c('Action').t`Export`}>
                        <Button
                            color="weak"
                            shape="ghost"
                            icon
                            onClick={handleExport}
                            className="inline-flex ml-2"
                            data-testid="contact-details:export"
                        >
                            <IcArrowUpFromSquare alt={c('Action').t`Export`} />
                        </Button>
                    </Tooltip>,
                    <Tooltip title={c('Action').t`Delete`}>
                        <Button
                            color="weak"
                            shape="ghost"
                            icon
                            onClick={handleDelete}
                            className="inline-flex ml-2"
                            data-testid="contact-details:delete"
                        >
                            <IcTrash alt={c('Action').t`Delete`} />
                        </Button>
                    </Tooltip>,
                ]}
            />
            <ModalTwoContent>
                <ErrorBoundary key={contactID} component={<GenericError className="pt-7 view-column-detail flex-1" />}>
                    <div ref={modalRef}>
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <ContactView
                                vCardContact={vCardContact as VCardContact}
                                contactID={contactID}
                                contactEmails={contactEmailsMap[contactID] as ContactEmail[]}
                                contactGroupsMap={contactGroupsMap}
                                ownAddresses={ownAddresses}
                                errors={errors}
                                isSignatureVerified={isVerified}
                                onReload={onReload}
                                onEdit={handleEdit}
                                onEmailSettings={onEmailSettings}
                                onGroupDetails={onGroupDetails}
                                onGroupEdit={onGroupEdit}
                                onUpgrade={onUpgrade}
                                onSignatureError={onSignatureError}
                                onDecryptionError={onDecryptionError}
                            />
                        )}
                    </div>
                    {linkModal}
                </ErrorBoundary>
            </ModalTwoContent>
            <ModalTwoFooter>
                <Button onClick={onClose}>{c('Action').t`Close`}</Button>
            </ModalTwoFooter>
        </ModalTwo>
    );
};

export default ContactDetailsModal;
